import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { authApi } from "../services/auth";
import { toast } from "sonner";
import {
  User as UserIcon, Settings, LogOut, ArrowRight, LayoutGrid, Shield, ChevronDown,
  Briefcase, Check, Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Personas in the order they're surfaced in the menu. Kept in sync with the
// backend allow-list in `auth_module.ALLOWED_PERSONAS`.
const PERSONAS = [
  { key: "council_member",     label: "Council member" },
  { key: "combined_authority", label: "Combined Authority" },
  { key: "dsit",               label: "DSIT / Government" },
  { key: "operator",           label: "Operator" },
  { key: "altnet",             label: "AltNet" },
  { key: "investor",           label: "Investor" },
  { key: "researcher",         label: "Researcher" },
  { key: "other",              label: "Other" },
];

function personaLabel(key) {
  return PERSONAS.find((p) => p.key === key)?.label || key;
}

/**
 * Role-aware user menu.
 *
 * Modes:
 *  - "standard" → Mounted inside `PublicShell`. Switch action: "Analyst mode"
 *    (admin + analyst roles only).
 *  - "analyst"  → Mounted inside the Analyst `Layout`. Switch action: "Standard mode".
 *
 * Anonymous users see a primary "Sign in" CTA instead.
 */
export default function UserMenu({ mode = "standard" }) {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [savingPersona, setSavingPersona] = useState(null); // key in flight, or null

  if (!user) {
    return (
      <Link
        to="/signin"
        data-testid="signin-link"
        className={
          mode === "standard"
            ? "inline-flex items-center gap-1.5 bg-brand text-white text-sm rounded-md px-3 sm:px-4 py-2 hover:bg-brand-deep transition-colors"
            : "inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm rounded-md px-3 sm:px-4 py-2 hover:opacity-90 transition-opacity"
        }
      >
        Sign in
        <ArrowRight className="h-3.5 w-3.5 hidden sm:inline" />
      </Link>
    );
  }

  const initials = getInitials(user);
  const isAdmin = user.role === "admin";

  const handleSignOut = async () => {
    try {
      await logout();
    } catch {
      /* noop */
    }
    navigate("/");
  };

  const handlePersona = async (key) => {
    if (savingPersona) return;
    if (key === user.persona) return; // no-op
    setSavingPersona(key);
    try {
      await authApi.updatePersona(key);
      await refresh();
      toast.success(`Persona updated to ${personaLabel(key)}`);
    } catch (e) {
      toast.error(`Could not update persona: ${e.message || "unknown error"}`);
    } finally {
      setSavingPersona(null);
    }
  };

  // Theme tokens — Standard uses the consumer brand palette, Analyst uses the
  // dark popover theme defined in shadcn.
  const triggerCls =
    mode === "standard"
      ? "inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1 text-sm font-medium text-brand hover:bg-brand-bg border border-brand-border transition-colors"
      : "inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1 text-sm font-medium text-foreground hover:bg-secondary/50 border border-border transition-colors";
  const avatarCls =
    mode === "standard"
      ? "h-7 w-7 rounded-full bg-brand text-white flex items-center justify-center text-[11px] font-mono font-semibold uppercase tracking-wider"
      : "h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-mono font-semibold uppercase tracking-wider";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="user-menu-trigger" className={triggerCls} aria-label="User menu">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <span className={avatarCls} aria-hidden="true">{initials}</span>
          )}
          <span className="hidden sm:inline-block max-w-[120px] truncate">
            {firstName(user) || user.email}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-64" data-testid="user-menu-content">
        {/* Identity card */}
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-start gap-3">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover mt-0.5" />
            ) : (
              <span
                className="h-9 w-9 rounded-full bg-brand text-white flex items-center justify-center text-xs font-mono font-semibold uppercase tracking-wider mt-0.5"
                aria-hidden="true"
              >
                {initials}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user.name || firstName(user) || "Signed in"}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              <div className="mt-1.5 inline-flex items-center gap-1.5">
                <RoleBadge role={user.role} />
                {user.persona ? (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-muted/60 rounded px-1.5 py-0.5">
                    {user.persona}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Mode switch */}
        {mode === "standard" ? (
          <DropdownMenuItem onSelect={() => navigate("/analyst")} data-testid="user-menu-analyst">
            <LayoutGrid className="h-4 w-4 mr-2" /> Analyst mode
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => navigate("/")} data-testid="user-menu-standard">
            <ArrowRight className="h-4 w-4 mr-2 rotate-180" /> Standard mode
          </DropdownMenuItem>
        )}

        {/* Profile */}
        <DropdownMenuItem onSelect={() => navigate("/profile")} data-testid="user-menu-profile">
          <UserIcon className="h-4 w-4 mr-2" /> My profile
        </DropdownMenuItem>

        {/* Inline persona switcher — same call as /profile but without leaving the page */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid="user-menu-persona-trigger">
            <Briefcase className="h-4 w-4 mr-2" />
            <span className="flex-1">Switch persona</span>
            {user.persona ? (
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground ml-2 max-w-[90px] truncate">
                {personaLabel(user.persona)}
              </span>
            ) : null}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="w-56" data-testid="user-menu-persona-list">
              {PERSONAS.map((p) => {
                const active = user.persona === p.key;
                const isSaving = savingPersona === p.key;
                return (
                  <DropdownMenuItem
                    key={p.key}
                    onSelect={(e) => { e.preventDefault(); handlePersona(p.key); }}
                    disabled={isSaving}
                    data-testid={`user-menu-persona-${p.key}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className={active ? "text-brand-accent font-medium" : ""}>{p.label}</span>
                    {isSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-accent" />
                    ) : active ? (
                      <Check className="h-3.5 w-3.5 text-brand-accent" />
                    ) : null}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {/* Admin */}
        {isAdmin ? (
          <DropdownMenuItem onSelect={() => navigate("/admin")} data-testid="user-menu-admin">
            <Settings className="h-4 w-4 mr-2" /> Admin portal
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={handleSignOut} data-testid="user-menu-signout" className="text-destructive focus:text-destructive">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function getInitials(user) {
  const src = (user.name || user.email || "").trim();
  if (!src) return "·";
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return src.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function firstName(user) {
  if (user.name) return user.name.split(/\s+/)[0];
  if (user.email) return user.email.split("@")[0].split(/[._-]/)[0];
  return null;
}

function RoleBadge({ role }) {
  const palette = {
    admin: "bg-brand-accent/15 text-brand-accent border-brand-accent/30",
    analyst: "bg-primary/15 text-primary border-primary/30",
    standard_user: "bg-muted/60 text-muted-foreground border-border",
  };
  const cls = palette[role] || palette.standard_user;
  const label = role === "standard_user" ? "Standard" : role;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider rounded px-1.5 py-0.5 border ${cls}`}>
      {role === "admin" ? <Shield className="h-2.5 w-2.5" /> : null}
      {label}
    </span>
  );
}
